import { User } from "lib/models/user";
import * as API from "lib/api/types";

export interface FormValues {
  email?: string;
  firstName?: string;
  chosenFirstName?: string;
  middleName?: string;
  lastName?: string;
  chosenLastName?: string;
  phone?: string;
  gender?: API.Gender;
  lgbtq?: API.GenericBooleanOption;
  primaryCity?: string;
  primaryCountry?: string;
  primaryLine1?: string;
  primaryLine2?: string;
  primaryZip?: string;
  primaryState?: string;
  mailingCity?: string;
  mailingCountry?: string;
  mailingLine1?: string;
  mailingLine2?: string;
  mailingZip?: string;
  mailingState?: string;
  mailingPreference?: API.MailingPreference;
  dlMailingPreference?: API.MagazinePreference;
  unionLeadership?: API.GenericBooleanOption;
  unionMember?: API.UnionMemberStatus;
  unionLocal?: string;
  unionName?: string;
}

type FormValuesArr = ReadonlyArray<keyof FormValues>;

export const accountFormKeys: FormValuesArr = [
  "email",
  "chosenFirstName",
  "chosenLastName",
] as const;

export const primaryInfoFormKeys: FormValuesArr = [
  "firstName",
  "lastName",
  "primaryLine1",
  "primaryLine2",
  "primaryCity",
  "primaryState",
  "primaryZip",
  "primaryCountry", // not included in form - assumes US
] as const;

export const postalPreferencesFormKeys: FormValuesArr = [
  "mailingPreference",
  "dlMailingPreference",
  "mailingLine1",
  "mailingLine2",
  "mailingCity",
  "mailingState",
  "mailingZip",
  "mailingCountry", // not included in form - assumes US
] as const;

export const mailingAddressFormKeys: FormValuesArr = [
  "mailingLine1",
  "mailingLine2",
  "mailingCity",
  "mailingState",
  "mailingZip",
  "mailingCountry", // not included in form - assumes US
] as const;

export const mailingOptionsFormKeys: FormValuesArr = [
  "mailingPreference",
  "dlMailingPreference",
] as const;

export const demographicsFormKeys: FormValuesArr = [
  "lgbtq",
  "gender",
  "unionMember",
  "unionLeadership",
  "unionName",
  "unionLocal",
] as const;

export type PickFormValues<T extends FormValuesArr> = Pick<FormValues, T[number]>;

export type AccountFormValues = PickFormValues<typeof accountFormKeys>;
export type PrimaryInfoFormValues = PickFormValues<typeof primaryInfoFormKeys>;
export type PostalPreferencesFormValues = PickFormValues<
  typeof postalPreferencesFormKeys
>;
export type MailingAddressFormValues = PickFormValues<typeof mailingAddressFormKeys>;
export type MailingOptionsFormValues = PickFormValues<typeof mailingOptionsFormKeys>;
export type DemographicsFormValues = PickFormValues<typeof demographicsFormKeys>;

export type PartialProfileFormValues =
  | AccountFormValues
  | PrimaryInfoFormValues
  | PostalPreferencesFormValues
  | DemographicsFormValues;

export const sliceProfileValues = <
  K extends FormValuesArr,
  R = Pick<FormValues, K[number]>,
>(
  keys: K,
  values: FormValues,
): R =>
  keys.reduce((acc, key) => {
    return { ...acc, [key]: values[key] };
  }, {} as R);

export const SerializeForm = {
  commonKeys: <T extends User["profile"] | FormValues>({
    email,
    firstName,
    lastName,
    middleName,
    chosenFirstName,
    chosenLastName,
    dlMailingPreference,
    mailingPreference,
  }: T): Pick<
    T,
    | "email"
    | "firstName"
    | "lastName"
    | "middleName"
    | "chosenFirstName"
    | "chosenLastName"
    | "dlMailingPreference"
    | "mailingPreference"
  > => ({
    email,
    firstName,
    lastName,
    middleName,
    chosenFirstName,
    chosenLastName,
    dlMailingPreference,
    mailingPreference,
  }),

  valuesToProfile: (
    values: FormValues,
    _only?: Array<keyof API.UserProfilePayload>,
  ): API.UserProfilePayload => {
    const isUnionMember = values.unionMember !== "0";

    const payload = {
      ...SerializeForm.commonKeys(values),
      primaryAddress: {
        line1: values.primaryLine1,
        line2: values.primaryLine2,
        city: values.primaryCity,
        state: values.primaryState,
        country: values.primaryCountry,
        zip: values.primaryZip,
      },
      mailingAddress: {
        line1: values.mailingLine1,
        line2: values.mailingLine2,
        city: values.mailingCity,
        state: values.mailingState,
        country: values.mailingCountry,
        zip: values.mailingZip,
      },
      demographicInfo: {
        gender: values.gender,
        lgbtq: values.lgbtq,
        unionMember: values.unionMember,
        // edge case: UX question. if they are not a union member do we want to send undefined, "" or null?
        // i think undefined values will cause previous values on the profile to remain.
        unionName: isUnionMember ? values.unionName : undefined,
        unionLocal: isUnionMember ? values.unionLocal : undefined,
        unionLeadership: isUnionMember ? values.unionLeadership : undefined,
      },
    };
    const nestedKeys = ["primaryAddress", "mailingAddress", "demographicInfo"] as const;

    nestedKeys.forEach((key) => {
      const isEmpty =
        Object.values(payload[key]).filter((value) => value !== undefined).length === 0;
      if (isEmpty) {
        delete payload[key];
      }
    });
    return payload;
  },

  valuesToMailingAddress: (
    values: MailingAddressFormValues,
  ): API.UserProfilePayload => ({
    mailingAddress: {
      city: values.mailingCity,
      country: values.mailingCountry,
      line1: values.mailingLine1,
      line2: values.mailingLine2,
      state: values.mailingState,
      zip: values.mailingZip,
    },
  }),

  valuesToMailingPreferences: (
    values: MailingOptionsFormValues,
  ): API.UserProfilePayload => ({
    dlMailingPreference: values.dlMailingPreference,
    mailingPreference: values.mailingPreference,
  }),

  profileToFormValues: (
    values: API.UserProfilePayload,
    only?: Array<keyof FormValues>,
  ): FormValues => {
    const { mailingAddress, primaryAddress, demographicInfo } = values;

    return Object.fromEntries(
      Object.entries({
        ...SerializeForm.commonKeys(values),
        mailingLine1: mailingAddress?.line1,
        mailingLine2: mailingAddress?.line2,
        mailingCity: mailingAddress?.city,
        mailingState: mailingAddress?.state,
        mailingCountry: mailingAddress?.country,
        mailingZip: mailingAddress?.zip,
        primaryLine1: primaryAddress?.line1,
        primaryLine2: primaryAddress?.line2,
        primaryCity: primaryAddress?.city,
        primaryState: primaryAddress?.state,
        primaryCountry: primaryAddress?.country,
        primaryZip: primaryAddress?.zip,
        lgbtq: demographicInfo?.lgbtq,
        gender: demographicInfo?.gender,
        unionMember: demographicInfo?.unionMember,
        unionLeadership: demographicInfo?.unionLeadership,
        unionName: demographicInfo?.unionName,
        unionLocal: demographicInfo?.unionLocal,
      }).filter(([key]) => !only || only?.includes(key as keyof FormValues)),
    );
  },
};
